import { z } from "zod";
import { csaSeasonSchema, pottedPlantTagSchema, pottedPlantTypeSchema, wreathDiameterSchema, wreathTagSchema, } from "../../inventory";
import { bloomSizeSchema, colorKeySchema, stemLengthSchema, stemsPerBunchSchema, vaseLifeSchema, } from "../../primitives";
const TWENTY_FOUR_HOURS_MS = 60e3 * 60 * 24;
const ONE_YEAR_MS = TWENTY_FOUR_HOURS_MS * 365;
const trimmedStringSchema = z.string().transform((val) => val.trim());
const nonEmptyTrimmedStringSchema = trimmedStringSchema.refine((val) => !!val);
const reasonablePriceSchema = z.number().int().positive().max(1000000); // $10,000
const stemCountSchema = z.number().int().min(1).max(10000);
const baseStagedListingSchema = z.object({
    description: trimmedStringSchema,
    photoStorageUrl: z.string().url(),
});
export const physicalAvailabilityFormSchema = z.object({
    active: z.boolean(),
    quantity: z.number().int().nonnegative(),
    dates: z
        .array(z.string())
        .length(2)
        .refine(([start, end]) => {
        const startTime = new Date(start).getTime();
        const endTime = new Date(end).getTime();
        return (Number.isFinite(startTime) &&
            Number.isFinite(endTime) &&
            startTime <= endTime &&
            // Give 2 days buffer -- it's not terrible if the availability is expired
            startTime > Date.now() - TWENTY_FOUR_HOURS_MS * 2 &&
            endTime < Date.now() + ONE_YEAR_MS);
    }, { message: "Invalid start / end dates" })
        // Antd defaults the RangePicker to null (for whatever reason), so this allows null or undefined
        // and coerces it to undefined
        .nullable()
        .optional()
        .transform((val) => (!val ? undefined : val)),
});
export const customAvailabilityFormSchema = z.object({
    active: z.boolean(),
    quantity: z.number().int().nonnegative(),
    shortName: z.string(),
    description: trimmedStringSchema,
});
const stagedCutFlowerListingSchema = baseStagedListingSchema.extend({
    category: z.literal("cut-flower"),
    price: reasonablePriceSchema,
    availability: physicalAvailabilityFormSchema,
    prototypeId: z.string(),
    dominantColor: colorKeySchema,
    stemsPerBunch: stemsPerBunchSchema,
    stemLength: stemLengthSchema,
    bloomSize: bloomSizeSchema.optional(),
    vaseLife: vaseLifeSchema.optional(),
});
const stagedBouquetListingSchema = baseStagedListingSchema.extend({
    category: z.literal("bouquet"),
    price: reasonablePriceSchema,
    availability: physicalAvailabilityFormSchema,
    prototypeId: z.string(),
    dominantColor: colorKeySchema,
    stemCount: stemCountSchema,
});
const stagedBucketListingSchema = baseStagedListingSchema
    .extend({
    category: z.literal("bucket"),
    price: reasonablePriceSchema,
    availability: physicalAvailabilityFormSchema,
    prototypeId: z.string(),
    dominantColor: colorKeySchema,
    minStems: z.number().int().positive(),
    maxStems: z.number().int().positive(),
})
    .refine((val) => val.minStems <= val.maxStems, {
    message: "'Min Stems' must be less than 'Max Stems'",
});
const baseStagedDriedFlowerListingSchema = baseStagedListingSchema.extend({
    category: z.literal("dried-flower"),
    price: reasonablePriceSchema,
    availability: physicalAvailabilityFormSchema,
    dominantColor: colorKeySchema,
});
const stagedSingleVarietyDriedFlowerListingSchema = baseStagedDriedFlowerListingSchema.extend({
    type: z.literal("single-variety"),
    prototypeId: z.string(),
    stemLength: stemLengthSchema,
    stemCount: stemCountSchema,
});
const stagedMixedBunchDriedFlowerListingSchema = baseStagedDriedFlowerListingSchema.extend({
    type: z.literal("mixed-bunch"),
    name: nonEmptyTrimmedStringSchema,
    stemLength: stemLengthSchema,
    stemCount: stemCountSchema,
});
const stagedConfettiDriedFlowerListingSchema = baseStagedDriedFlowerListingSchema.extend({
    type: z.literal("confetti"),
    name: nonEmptyTrimmedStringSchema,
});
export const stagedDriedFlowerListingSchema = z.union([
    stagedSingleVarietyDriedFlowerListingSchema,
    stagedMixedBunchDriedFlowerListingSchema,
    stagedConfettiDriedFlowerListingSchema,
]);
const stagedWreathListingSchema = baseStagedListingSchema.extend({
    category: z.literal("wreath"),
    name: nonEmptyTrimmedStringSchema,
    price: reasonablePriceSchema,
    availability: physicalAvailabilityFormSchema,
    diameter: wreathDiameterSchema,
    tags: wreathTagSchema.array(),
    depth: z.number().int().min(1).max(30).optional(),
});
const stagedPottedPlantListingSchema = baseStagedListingSchema.extend({
    category: z.literal("potted-plant"),
    name: nonEmptyTrimmedStringSchema,
    price: reasonablePriceSchema,
    availability: physicalAvailabilityFormSchema,
    pottedPlantType: pottedPlantTypeSchema,
    tags: pottedPlantTagSchema.array().optional(),
    potSize: z.number().positive().optional(),
    traySize: z.number().int().positive().optional(),
});
const stagedCSAListingSchema = baseStagedListingSchema.extend({
    category: z.literal("csa"),
    name: nonEmptyTrimmedStringSchema,
    price: reasonablePriceSchema,
    availability: customAvailabilityFormSchema,
    season: csaSeasonSchema,
    numberOfWeeks: z.number().int().min(1).max(12),
});
// TODO: make this a union with other listing types
export const stagedListingSchema = z.union([
    stagedCutFlowerListingSchema,
    stagedBouquetListingSchema,
    stagedBucketListingSchema,
    stagedDriedFlowerListingSchema,
    stagedWreathListingSchema,
    stagedPottedPlantListingSchema,
    stagedCSAListingSchema,
]);
//# sourceMappingURL=addListings.js.map